import type { FieldErrors, FieldValues } from "react-hook-form";

const FLATTEN_DEV_PREFIX = "[Form/flattenFieldErrors]";

function warnFlattenDev(message: string, detail?: unknown): void {
    if (process.env.NODE_ENV === "production") return;
    if (detail !== undefined) {
        console.warn(FLATTEN_DEV_PREFIX, message, detail);
    } else {
        console.warn(FLATTEN_DEV_PREFIX, message);
    }
}

/**
 * Одна строка после разворота {@link FieldErrors} в плоский список.
 */
export type FlattenedFieldError = {
    /** Путь поля в нотации RHF (`loginName`, `items.0.name`, …). */
    path: string;
    message: string;
};

function isNestedErrorNode(value: unknown): value is Record<string, unknown> {
    return value != null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Разворачивает дерево {@link FieldErrors} из react-hook-form в плоский список
 * `{ path, message }`, чтобы строить тексты вида «поле + сообщение» без ручного обхода.
 *
 * **Модель обхода (что считаем узлом дерева)**
 * - Корень — всегда plain-object (как у RHF после resolver). Корень-массив не поддерживаем: в DEV предупреждение и `[]`.
 * - На каждом объекте сначала, если есть непустая строка `message`, добавляем строку с текущим `path` (корень → `(form)`).
 * - Далее обходим **собственные** ключи объекта, пропуская служебные RHF: `message`, `type`, `types`, `ref` (иначе циклы и дубли).
 * - Значение по ключу может быть:
 *   - **объект** (не массив, не Date) — рекурсия, сегмент пути = `key`;
 *   - **массив** — типичный случай `useFieldArray`: `errors.points[i]` — объект ошибок по полям элемента. Обходим индексы `0..length-1`,
 *     для каждого элемента-объекта рекурсия с сегментом `key.index` (в нотации RHF: `points.0.site`). Пустые слоты и не-объекты пропускаем.
 * - Ветку `types` (несколько сообщений на одно поле при `criteriaMode: 'all'`) **не** раскладываем: в плоском списке остаётся только явный `message` на узле.
 *
 * **Острые моменты**
 * - Если пришло не то (не объект на корне) — в DEV пишем в консоль и возвращаем `[]`, чтобы вызывающий не падал.
 * - Если после обхода есть ключи верхнего уровня, но ни одного непустого листового `message` — в DEV одно предупреждение (часто неверная форма ошибок или только «пустые» узлы).
 *
 * @template TFieldValues shape значений формы
 * @param errors объект ошибок с сабмита / resolver
 */
export function flattenFieldErrors<TFieldValues extends FieldValues>(
    errors: FieldErrors<TFieldValues>,
): FlattenedFieldError[] {
    if (errors == null) {
        warnFlattenDev("Ожидался объект ошибок RHF, получено null/undefined — плоский список пуст.");
        return [];
    }
    if (typeof errors !== "object" || Array.isArray(errors)) {
        warnFlattenDev("Ожидался объект ошибок RHF, получен не-object / массив — плоский список пуст.", errors);
        return [];
    }

    const out: FlattenedFieldError[] = [];

    function visit(node: unknown, path: string): void {
        if (!isNestedErrorNode(node)) return;

        const msg = node.message;
        if (typeof msg === "string" && msg !== "") {
            out.push({ path: path !== "" ? path : "(form)", message: msg });
        }

        for (const key of Object.keys(node)) {
            if (key === "message" || key === "type" || key === "types" || key === "ref") continue;
            const child = node[key];
            if (Array.isArray(child)) {
                for (let i = 0; i < child.length; i++) {
                    const item = child[i];
                    if (!isNestedErrorNode(item)) continue;
                    const segment = path !== "" ? `${path}.${key}.${i}` : `${key}.${i}`;
                    visit(item, segment);
                }
            } else if (isNestedErrorNode(child)) {
                visit(child, path !== "" ? `${path}.${key}` : key);
            }
        }
    }

    visit(errors, "");
    if (out.length === 0 && Object.keys(errors as object).length > 0) {
        warnFlattenDev(
            "В дереве ошибок есть ключи, но ни одного листа с непустым `message` — проверьте форму ошибок resolver / кастомный `setError`.",
            errors,
        );
    }
    return out;
}
